import React, { createContext, useContext, useMemo } from 'react'
import { NavDropdown as BootstrapNavDropdown } from 'react-bootstrap'
import type { NavDropdownProps as BootstrapNavDropdownProps, DropdownItemProps as BootstrapNavDropdownItemProps } from 'react-bootstrap'
import { Groups } from '@utils/groups';
import { User } from '../../payload-types';
import { hasAccessTo } from '@utils/groups';

type NavDropdownProps = BootstrapNavDropdownProps & { user?: User, groups?: Groups | Groups[] | null };
type NavDropdownItemProps = BootstrapNavDropdownItemProps & { target?: HTMLAnchorElement['target'], groups?: Groups | Groups[] | null };

type NavDropdownItemComponent = React.ForwardRefExoticComponent<React.PropsWithoutRef<NavDropdownItemProps> & React.RefAttributes<HTMLAnchorElement>>;

type NavDropdownComponent = React.ForwardRefExoticComponent<
  React.PropsWithoutRef<NavDropdownProps> & React.RefAttributes<HTMLDivElement>
> & {
  Item: NavDropdownItemComponent;
  ItemText: typeof BootstrapNavDropdown.ItemText;
  Divider: typeof BootstrapNavDropdown.Divider;
  Header: typeof BootstrapNavDropdown.Header;
};

// Context
const NavDropdownContext = createContext<{ user?: User }>({});

const useNavDropdownContext = () => useContext(NavDropdownContext);

const NavDropdown = React.forwardRef<HTMLDivElement, NavDropdownProps>(
  ({ user, groups = null, children, ...props }: NavDropdownProps, ref) => {
    const contextValue = useMemo(() => ({ user }), [user]);

    groups = groups ? ( Array.isArray(groups) ? groups.flat() : [ groups ] ) : null;

    if (user && !hasAccessTo(user, groups)) {
      return null;
    }

    return (
      <NavDropdownContext.Provider value={contextValue}>
        <BootstrapNavDropdown {...props} ref={ref}>
          {children}
        </BootstrapNavDropdown>
      </NavDropdownContext.Provider>
    );
  }
) as NavDropdownComponent;

const NavDropdownItem = React.forwardRef<HTMLAnchorElement, NavDropdownItemProps>(
  ({ groups = null, children, ...props }: NavDropdownItemProps, ref) => {
    const { user } = useNavDropdownContext();

    groups = groups ? ( Array.isArray(groups) ? groups.flat() : [ groups ] ) : null;

    if (user && ! hasAccessTo(user, groups)) {
      return null;
    }

    return <BootstrapNavDropdown.Item {...props} ref={ref}>{children}</BootstrapNavDropdown.Item>;
  }
) as NavDropdownItemComponent;

NavDropdown.Item = NavDropdownItem;
NavDropdown.ItemText = BootstrapNavDropdown.ItemText;
NavDropdown.Divider = BootstrapNavDropdown.Divider;
NavDropdown.Header = BootstrapNavDropdown.Header;

export default NavDropdown
